import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  PLATFORM_ID,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SongsService } from '../../services/songs.service';
import { LayoutService } from '../../services/layout.service';
import { BadgeComponent } from '../badge/badge.component';
import { ButtonComponent } from '../button/button.component';
import { formatNumber } from '../../utils/formatters';

@Component({
  selector: 'app-song-profile-preview-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, BadgeComponent, ButtonComponent],
  templateUrl: './song-profile-preview-drawer.component.html',
  host: {
    class: 'contents',
  },
})
export class SongProfilePreviewDrawerComponent {
  private songsService = inject(SongsService);
  private layoutService = inject(LayoutService);
  private platformId = inject(PLATFORM_ID);

  readonly songChartsFixedPush = input(false, { transform: booleanAttribute });
  readonly mode = input<'inline' | 'fixed' | 'auto'>('auto');
  readonly sidebarOpenChange = output<boolean>();

  selectedSongId = signal<string | null>(null);
  songDetails = signal<any | null>(null);
  loading = signal<boolean>(false);
  hasOpened = signal<boolean>(false);

  readonly isOpen = computed(() => this.selectedSongId() !== null);

  readonly isFixed = computed(() => {
    const m = this.mode();
    if (m === 'fixed') return true;
    if (m === 'inline') return false;
    return this.songChartsFixedPush();
  });

  readonly chartSkeletonBarKeys = Array.from({ length: 12 }, (_, i) => i);

  hoveredPoint = signal<{ x: number; y: number; date: string; rank: number } | null>(null);

  readonly asideNgClass = computed(() => {
    const open = this.isOpen();
    const hasOpened = this.hasOpened();
    const isClosed = !open && !this.songDetails();
    if (this.isFixed()) {
      return {
        'fixed top-[88px] right-4 z-[100] flex w-full md:w-[380px] md:h-[calc(100vh-104px)] md:rounded-2xl md:border flex-col overflow-hidden bg-sidebar-panel-bg max-md:right-0 max-md:top-[72px] max-md:h-[calc(100vh-72px)] max-md:border-l max-md:border-[#27272A] max-md:shadow-2xl':
          true,
        'transition-transform duration-300 ease-in-out': hasOpened,
        'translate-x-0 border-sidebar-panel-border song-drawer-fixed-shadow md:border-sidebar-panel-border': open,
        'translate-x-full md:translate-x-[calc(100%+1rem)] pointer-events-none border-transparent shadow-none max-md:border-transparent md:border-transparent md:shadow-none':
          !open,
      };
    }
    return {
      'max-md:fixed max-md:inset-y-0 max-md:right-0 max-md:z-[250] max-md:w-full max-md:border-sidebar-panel-border max-md:border-l bg-sidebar-panel-bg': true,
      'transition-all duration-300 ease-in-out will-change-[transform,opacity] transform-gpu': hasOpened,
      'max-md:translate-x-0': open,
      'max-md:translate-x-full max-md:pointer-events-none': !open,
      'md:absolute md:inset-0 md:w-full md:h-full md:min-h-0 md:overflow-hidden md:border md:border-sidebar-panel-border md:shadow-xl md:rounded-xl': true,
      'md:opacity-100 md:z-30 opacity-100': open,
      'md:opacity-0 md:z-10 md:pointer-events-none opacity-0 pointer-events-none': !open,
    };
  });

  readonly songHistoryPoints = computed(() => {
    const details = this.songDetails();
    const positions = details ? (details.postitions || details.positions || []) : [];
    if (positions.length === 0) return [];

    const width = 520;
    const height = 180;
    const padding = 15;
    const chartHeight = height - padding * 2;

    const stepX = positions.length > 1 ? width / (positions.length - 1) : 0;

    return positions.map((record: any, index: number) => {
      const rank = record.rank || 100;
      const clampedRank = Math.min(100, Math.max(1, rank));
      
      const x = positions.length === 1 ? width / 2 : index * stepX;
      // Invert Y axis: rank 1 at the top, rank 100 at the bottom
      const y = padding + ((clampedRank - 1) / 99) * chartHeight;
      return {
        x,
        y,
        rank,
        date: `W${record.week} ${record.year}`,
      };
    });
  });

  readonly songTrendPoints = computed(() => {
    const points = this.songHistoryPoints();
    if (points.length === 0) return '';
    return points.map((p: any) => `${p.x},${p.y}`).join(' ');
  });

  async open(id: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.hasOpened.set(true);
    const resolvedId = String(id).trim();
    if (!resolvedId) return;

    this.selectedSongId.set(resolvedId);
    this.sidebarOpenChange.emit(true);
    requestAnimationFrame(() => {
      document.getElementById('song-profile-preview-drawer-scroll')?.scrollTo({ top: 0 });
    });

    this.loading.set(true);
    this.songDetails.set(null);
    this.hoveredPoint.set(null);

    try {
      const response = await firstValueFrom(this.songsService.getSongDetailed(resolvedId));
      if (this.selectedSongId() !== resolvedId) return;
      this.songDetails.set(response.data?.song ?? null);
    } catch (error) {
      console.error('Failed to load song details:', error);
    } finally {
      if (this.selectedSongId() === resolvedId) {
        this.loading.set(false);
      }
    }
  }

  closeSidebar(): void {
    this.selectedSongId.set(null);
    this.sidebarOpenChange.emit(false);
    this.layoutService.isFixedDrawerPaddingActive.set(false);
    setTimeout(() => {
      this.songDetails.set(null);
      this.loading.set(false);
      this.hoveredPoint.set(null);
    }, 300);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.isOpen() && this.isFixed()) {
      this.closeSidebar();
    }
  }

  onChartMouseMove(event: MouseEvent): void {
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    
    const clientX = event.clientX - rect.left;
    const svgX = (clientX / rect.width) * 520;

    const points = this.songHistoryPoints();
    if (points.length === 0) return;

    let closestPoint = points[0];
    let minDiff = Math.abs(points[0].x - svgX);

    for (let i = 1; i < points.length; i++) {
      const diff = Math.abs(points[i].x - svgX);
      if (diff < minDiff) {
        minDiff = diff;
        closestPoint = points[i];
      }
    }

    this.hoveredPoint.set({
      x: closestPoint.x,
      y: closestPoint.y,
      date: closestPoint.date,
      rank: closestPoint.rank,
    });
  }

  onChartMouseLeave(): void {
    this.hoveredPoint.set(null);
  }

  formatDuration(durationMs: number | null): string {
    if (!durationMs) return '--:--';
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  formatStreams(value: number | null | undefined): string {
    return formatNumber(value);
  }

  getArtistsList(artists: any[]): string {
    if (!artists || !artists.length) return '';
    return artists.map((a) => a.name).join(', ');
  }

  getAwardLabel(aw: any): string {
    let typeStr = aw.award;
    if (aw.award === 'D') typeStr = '💎 Diamond';
    else if (aw.award === 'P' || (typeof aw.award === 'string' && aw.award.includes('P'))) typeStr = `💿 Platinum (${aw.award})`;
    else if (aw.award === 'G') typeStr = '🏆 Gold';
    else typeStr = `🎖️ ${aw.award}`;

    if (aw.week && aw.year) {
      return `${typeStr} • W${aw.week} ${aw.year}`;
    }
    return typeStr;
  }

  playOnSpotify(): void {
    const details = this.songDetails();
    if (details?.spotifyId && isPlatformBrowser(this.platformId)) {
      window.open(`https://open.spotify.com/track/${details.spotifyId}`, '_blank', 'noopener,noreferrer');
    }
  }

  songImageFallback(title: string): string {
    const n = (title || 'S').trim() || 'S';
    return `https://placehold.co/300x300/1e293b/94a3b8?text=${encodeURIComponent(n.substring(0, 10))}`;
  }

  onDrawerImageError(event: Event, title: string): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = this.songImageFallback(title);
    }
  }
}
